/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { pick, omit } from 'lodash';
import {
  CrudAddType,
  CrudEditType,
  CrudListType,
  CrudRemoveType,
  CrudTemplate,
  ListColumns
} from '../../../organisms/crud/form-types';

import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import moment from 'moment';
import { TFunction } from 'react-i18next';
import { formFields } from './form-fields';
import { Drug } from 'app/types/drug';
import { normalizeTemplate } from 'app/components/organisms/crud/utils';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import React from 'react';
import { CCIText } from 'app/types/cci';
import { Description } from './components/description';
import { IdCopy } from 'app/components/atoms/id-copy/id-copy';
import { useCrudRequest } from 'app/hooks/app-requests/crud/use-crud-request';

/**
 * List labels.
 */

const listColumns = (translate: TFunction): ListColumns<CCIText> => [
  {
    title: translate('common.table.columns.id'),
    dataIndex: 'id',
    key: 'id',
    size: '180px',
    render: item => {
      return <IdCopy>{item.id}</IdCopy>;
    }
  },
  {
    title: translate('common.table.columns.description'),
    dataIndex: 'description',
    supportLocale: true,
    key: 'description',
    size: '1fr',
    render: item => <Description description={item.description} />
  },
  {
    title: translate('common.table.columns.relations'),
    dataIndex: 'relations',
    supportLocale: true,
    key: 'relations',
    size: '1fr',
    render: item => item.relations?.length
  },
  {
    title: translate('common.table.columns.createdAt'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    size: '200px',
    render: item => <DateTime date={item.createdAt} />
  },
  {
    title: translate('common.table.columns.updatedAt'),
    dataIndex: 'updatedAt',
    key: 'updatedAt',
    size: '200px',
    render: item => <DateTime date={item.updatedAt} />
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: translate('common.labels.search')
  },
  {
    type: 'selectSearch',
    name: 'cci',
    inline: true,
    style: {
      minWidth: 230
    },
    label: translate('common.labels.cci'),
    showSearch: true,
    filterOption: (input, option) => {
      return String(option?.value ?? '')
        .toLowerCase()
        .startsWith(input.toLowerCase());
    },
    allowClear: true,
    options: {
      hook: useCrudRequest as any,
      hookProps: () => {
        return [
          {
            key: ['ccis'],
            endpoint: apiEndpoints.ccisCodes
          }
        ];
      },
      normalize: (items: any) => [
        {
          label: translate('common.labels.empty'),
          value: null
        },
        ...(items?.map((item: any) => ({
          value: item.cci,
          label: item.cci
        })) ?? [])
      ]
    }
  }
];

/**
 * List.
 */

const list = (translate: TFunction): CrudListType<CCIText> => ({
  columns: listColumns(translate),
  filters: listFilters(translate),
  languages: '*',
  enableExport: true,
  route: appRoutes.dashboard.medical.ccis,
  addButton: translate('common.actions.add'),
  canEdit: true,
  canRemove: true,
  key: ['ccis'],
  pagination: {
    pageSize: 25,
    pageSizeOptions: [10, 25, 50, 100, 500, 1000]
  },
  endpoint: apiEndpoints.ccis,
  endpointById: apiEndpoints.ccisById
});

/**
 * Remove.
 */

const remove = (): CrudRemoveType<CCIText> => ({
  endpoint: apiEndpoints.ccisById,
  resolveParams: (params: any, item?: CCIText) => {
    return {
      id: params?.id ?? item?.id
    };
  }
});

/**
 * Normalize payload.
 */

function normalizePayload(values: any) {
  const result = omit(values, ['id', 'createdAt', 'updatedAt']);

  if (result.relations) {
    result.relations = result.relations.map((relation: any) => {
      return omit(relation, ['id', 'createdAt', 'updatedAt']);
    });
  }

  return result;
}

/**
 * Add.
 */

const add = (translate: TFunction): CrudAddType<CCIText> => ({
  formFields: formFields(translate),
  languages: '*',
  acceptQueryFields: [
    {
      name: 'accountId',
      parser: value => (!!value ? parseInt(value, 10) : undefined)
    }
  ],
  normalizePayload
});

/**
 * Edit.
 */

const edit = (translate: TFunction): CrudEditType<CCIText> => ({
  formFields: formFields(translate),
  languages: '*',
  endpoint: apiEndpoints.ccisById,
  redirect: appRoutes.dashboard.medical.ccis,
  resolveParams: (params: any) => params,
  route: appRoutes.dashboard.medical.ccisEdit,
  submitLabel: translate('common.actions.edit'),
  normalizePayload,
  normalizeInitialValues: (keys: string[], payload: Drug) => {
    const data: any = pick(payload, [...keys, 'relations']);

    data.createdAt = moment(data.createdAt).format('YYYY-MM-DD HH:mm:ss');
    data.updatedAt = moment(data.updatedAt).format('YYYY-MM-DD HH:mm:ss');

    return data;
  }
});

/**
 * Config.
 */

export function createCCIsTemplate(translate: TFunction): CrudTemplate<CCIText> {
  return normalizeTemplate(translate, {
    add: add(translate),
    edit: edit(translate),
    list: list(translate),
    remove: remove()
  });
}
